import {
  createUnifiedTheme,
  genPageTheme,
  shapes,
  palettes,
} from '@backstage/theme';

export const gezitTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: {
      main: '#D92D20',
      light: '#FEE5E2',
      dark: '#B91C1C',
    },
    secondary: {
      main: '#0F172A',
      light: '#334155',
      dark: '#020617',
    },
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0F172A',   // WCAG AAA: 17.2:1 contrast ratio on white
      secondary: '#334155', // WCAG AAA: 7.1:1 contrast ratio on white
      disabled: '#64748B',
    },
    navigation: {
      background: '#FFFFFF',
      indicator: '#D92D20',
      color: '#334155',
      selectedColor: '#D92D20',
      navItem: {
        hoverBackground: '#FEF2F2',
      },
    },
    status: {
      ok: '#15803D',
      warning: '#B45309',
      error: '#B91C1C',
      running: '#1D4ED8',
      pending: '#475569',
      aborted: '#475569',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        /* ========================================================= */
        /* PURE WHITE FLYOUT DRAWER & MY GROUPS SUBMENU OVERRIDES     */
        /* ========================================================= */

        /* Target ALL popovers, drawers, paper elements, submenus, and flyout overlays */
        .MuiDrawer-paper,
        .MuiDrawer-paperAnchorLeft,
        .MuiPopover-paper,
        .MuiPaper-root,
        div[class*="sidebarSubList"],
        div[class*="SidebarSubList"],
        div[class*="sidebarSubmenu"],
        div[class*="SidebarSubmenu"],
        div[class*="MyGroupsSidebarItem"],
        div[class*="myGroupsSidebarItem"],
        div[class*="drawer-"],
        div[class*="Drawer-"],
        div[class*="popover-"],
        div[class*="Popover-"] {
          background-color: #FFFFFF !important;
          background: #FFFFFF !important;
          color: #0F172A !important;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1) !important;
          border: 1px solid #E2E8F0 !important;
        }

        /* Force every container inside sidebar submenus and drawers to have white background */
        [class*="sidebar"] div,
        [class*="Sidebar"] div,
        [class*="Submenu"] div,
        [class*="submenu"] div,
        [class*="Drawer"] div,
        [class*="drawer"] div,
        [class*="Popover"] div,
        [class*="popover"] div {
          background-color: #FFFFFF !important;
        }

        /* Target Typography / Text inside drawers & submenus */
        [class*="sidebar"] *,
        [class*="Sidebar"] *,
        [class*="Submenu"] *,
        [class*="submenu"] *,
        [class*="Drawer"] *,
        [class*="drawer"] *,
        [class*="Popover"] *,
        [class*="popover"] * {
          color: #0F172A;
        }

        /* Submenu Headers & Titles */
        [class*="sidebar"] [class*="title"],
        [class*="Sidebar"] [class*="title"],
        [class*="Submenu"] [class*="title"],
        [class*="submenu"] [class*="title"],
        [class*="sidebar"] [class*="header"],
        [class*="Sidebar"] [class*="header"],
        [class*="Submenu"] [class*="header"],
        [class*="submenu"] [class*="header"],
        .MuiTypography-h6,
        .MuiTypography-subtitle1 {
          color: #0F172A !important;
          font-weight: 700 !important;
        }

        /* Submenu Links & List Items */
        [class*="sidebar"] a,
        [class*="Sidebar"] a,
        [class*="Submenu"] a,
        [class*="submenu"] a,
        [class*="Drawer"] a,
        [class*="drawer"] a,
        [class*="Popover"] a,
        [class*="popover"] a,
        .MuiListItem-root {
          color: #1E293B !important;
          font-weight: 500 !important;
        }

        /* SVG Icons inside submenus */
        [class*="sidebar"] svg,
        [class*="Sidebar"] svg,
        [class*="Submenu"] svg,
        [class*="submenu"] svg,
        [class*="Drawer"] svg,
        [class*="drawer"] svg,
        [class*="Popover"] svg,
        [class*="popover"] svg,
        .MuiSvgIcon-root {
          color: #64748B !important;
        }

        /* Hover state: Light red highlight background with red text */
        [class*="sidebar"] a:hover,
        [class*="Sidebar"] a:hover,
        [class*="Submenu"] a:hover,
        [class*="submenu"] a:hover,
        [class*="Drawer"] a:hover,
        [class*="drawer"] a:hover,
        .MuiListItem-root:hover {
          color: #D92D20 !important;
          background-color: #FEF2F2 !important;
          background: #FEF2F2 !important;
        }

        [class*="sidebar"] a:hover *,
        [class*="Sidebar"] a:hover *,
        [class*="Submenu"] a:hover *,
        [class*="submenu"] a:hover *,
        [class*="Drawer"] a:hover *,
        [class*="drawer"] a:hover *,
        .MuiListItem-root:hover * {
          color: #D92D20 !important;
        }

        /* Selected active group item: Solid red background with bold white text */
        [class*="sidebar"] a.Mui-selected,
        [class*="Sidebar"] a.Mui-selected,
        [class*="Submenu"] a.Mui-selected,
        [class*="submenu"] a.Mui-selected,
        .MuiListItem-root.Mui-selected {
          color: #FFFFFF !important;
          background-color: #D92D20 !important;
          background: #D92D20 !important;
        }

        [class*="sidebar"] a.Mui-selected *,
        [class*="Sidebar"] a.Mui-selected *,
        [class*="Submenu"] a.Mui-selected *,
        [class*="submenu"] a.Mui-selected *,
        .MuiListItem-root.Mui-selected * {
          color: #FFFFFF !important;
          font-weight: 700 !important;
        }

        /* Secondary text contrast enhancement */
        .MuiTypography-colorTextSecondary {
          color: #334155 !important;
        }

        /* High contrast table headers and borders */
        .MuiTableCell-head {
          color: #0F172A !important;
          font-weight: 700 !important;
          background-color: #F1F5F9 !important;
        }

        /* Link visibility & contrast */
        a {
          color: #B91C1C;
        }
        a:hover {
          color: #991B1B;
          text-decoration: underline;
        }
      `,
    },
  },
  fontFamily:
    '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({ colors: ['#D92D20', '#B91C1C'], shape: shapes.wave }),
    documentation: genPageTheme({
      colors: ['#D92D20', '#991B1B'],
      shape: shapes.wave2,
    }),
    tool: genPageTheme({ colors: ['#D92D20', '#0F172A'], shape: shapes.round }),
    service: genPageTheme({
      colors: ['#D92D20', '#B91C1C'],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: ['#D92D20', '#B91C1C'],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: ['#D92D20', '#B91C1C'],
      shape: shapes.wave,
    }),
    other: genPageTheme({
      colors: ['#D92D20', '#B91C1C'],
      shape: shapes.wave,
    }),
    app: genPageTheme({ colors: ['#D92D20', '#B91C1C'], shape: shapes.wave }),
  },
});
